import { useState } from 'react';

import { Field, formatEuro, Input, Select, Textarea } from '@/design-system';
import {
    isNhgClassification,
    isUrgency,
    NHG_CLASSIFICATION_LABELS,
    NHG_CLASSIFICATIONS,
    parseEuroToCents,
    URGENCIES,
    URGENCY_LABELS,
    type NhgClassification,
    type Urgency,
} from '@/entities/finding';

import { surveyCopy } from '../copy';
import type { PackageQuestion } from '../engine';
import { useDraftText } from '../hooks/useDraftText';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { VoiceButton } from './VoiceButton';

export interface FindingDetailProps {
    question: PackageQuestion;
    note: string;
    /** Waar het gebrek zit, in de woorden van de inspecteur; leeg als hij niets zei. */
    location: string;
    /** Het bedrag van de bevinding van dit antwoord, in hele centen. */
    amountCents: number;
    urgency: Urgency | null;
    nhg: NhgClassification | null;
    /** Het bedrag is dat van de inspecteur zelf, niet dat van de vragenlijst. */
    ownAmount: boolean;
    /** Een ingediende opname is alleen nog te lezen. */
    readOnly?: boolean;
    onNoteChange: (text: string) => void;
    onLocationChange: (text: string) => void;
    /** `null`: het veld is leeggemaakt, het bedrag uit de norm geldt weer. */
    onAmountChange: (cents: number | null) => void;
    onUrgencyChange: (urgency: Urgency) => void;
    onNhgChange: (nhg: NhgClassification | null) => void;
}

/**
 * Eén blok bij een aandachtspunt of gebrek: wat je ziet, waar het zit, en wat
 * het kost. Niet meer dan dat (principe 3).
 *
 * De bevinding en de plek staan open, want die schrijft de inspecteur zelf. Het
 * geld staat dicht: bedrag en urgentie rekent de vragenlijst uit, dus ze horen
 * op één rustige regel en niet als drie invulvelden bij elke vraag. Die regel
 * zegt één keer waar ze vandaan komen en zet "Aanpassen" ernaast - het rapport
 * is een juridisch document, dus corrigeren moet altijd één tik weg zijn.
 *
 * Tekstvelden schrijven op `blur`, zodat er één bewerking per wijziging in de
 * outbox komt en niet één per toetsaanslag. Wat hier staat, schrijft de app als
 * overschrijving op het antwoord; de API zet het op de bevinding van dat
 * antwoord.
 */
export function FindingDetail({
    question,
    note,
    location,
    amountCents,
    urgency,
    nhg,
    ownAmount,
    readOnly = false,
    onNoteChange,
    onLocationChange,
    onAmountChange,
    onUrgencyChange,
    onNhgChange,
}: FindingDetailProps) {
    const copy = surveyCopy.detail;
    const [text, setText] = useDraftText(note);
    /*
     * De plek, wat er het laatst is bewaard, en de laatst geziene prop. De prop
     * loopt een tel achter op de opslag: verandert hij naar wat deze kaart zelf
     * net schreef, dan is er niets nieuws en blijft wat de inspecteur intussen
     * typte staan. Alleen een andere waarde (van een ander apparaat) vervangt het
     * veld.
     */
    const [place, setPlace] = useState(location);
    const [savedPlace, setSavedPlace] = useState(location);
    const [seenLocation, setSeenLocation] = useState(location);

    if (location !== seenLocation) {
        setSeenLocation(location);

        if (location !== savedPlace) {
            setSavedPlace(location);
            setPlace(location);
        }
    }
    const shownCost = formatCost(amountCents);
    const [cost, setCost] = useDraftText(shownCost);
    const [adjusting, setAdjusting] = useState(false);

    const noteVoice = useVoiceInput((transcript) => {
        setText((current) => {
            const next = append(current, transcript);
            onNoteChange(next);

            return next;
        });
    });

    const savePlace = (next: string) => {
        if (next !== savedPlace) {
            setSavedPlace(next);
            onLocationChange(next);
        }
    };

    /* Eén opname tegelijk: de andere knop stopt zodra deze begint. */
    const placeVoice = useVoiceInput((transcript) => {
        setPlace((current) => {
            const next = append(current, transcript);
            savePlace(next);

            return next;
        });
    });

    return (
        <div
            data-testid="finding-detail"
            className="flex flex-col gap-2 border-t border-[var(--border-subtle)] bg-[var(--surface-detail)] px-3 py-[10px]"
        >
            <div className="flex flex-wrap items-center justify-between gap-[10px]">
                <span className="text-[12px] text-[var(--text-secondary)]">{copy.title}</span>

                {readOnly ? null : (
                    <VoiceButton
                        voice={{ ...noteVoice, toggle: exclusive(noteVoice, placeVoice) }}
                        idle={copy.voiceIdle}
                        recording={copy.voiceRecording}
                        unavailable={copy.voiceUnavailable}
                    />
                )}
            </div>

            <Textarea
                aria-label={`${copy.title} — ${question.label}`}
                placeholder={copy.notePlaceholder}
                value={text}
                readOnly={readOnly}
                onChange={(event) => setText(event.target.value)}
                onBlur={() => {
                    if (text !== note) {
                        onNoteChange(text);
                    }
                }}
            />

            {/* Waar het zit staat naast de tekst, want het hoort bij wat je ziet - niet bij het geld. */}
            <div className="flex items-end gap-[10px]">
                <Field label={copy.location} className="min-w-0 flex-1">
                    <Input
                        placeholder={copy.locationPlaceholder}
                        value={place}
                        readOnly={readOnly}
                        onChange={(event) => setPlace(event.target.value)}
                        onBlur={() => savePlace(place)}
                    />
                </Field>

                {readOnly ? null : (
                    <VoiceButton
                        voice={{ ...placeVoice, toggle: exclusive(placeVoice, noteVoice) }}
                        idle={copy.locationVoiceIdle}
                        recording={copy.voiceRecording}
                        unavailable={copy.locationVoiceUnavailable}
                        label={`${copy.locationVoiceIdle} — ${question.label}`}
                    />
                )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-[10px] border-t border-[var(--border-subtle)] pt-[10px]">
                <p className="m-0 min-w-0 text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                    <span className="font-[family-name:var(--font-mono)] text-[var(--text-primary)]">
                        {amountCents === 0 ? copy.costsNone : formatCost(amountCents)}
                    </span>
                    {urgency ? ` · ${URGENCY_LABELS[urgency]}` : ''}
                    {` · ${ownAmount ? copy.costsOwn : copy.costsAuto}`}
                </p>

                {readOnly ? null : (
                    <button
                        type="button"
                        aria-expanded={adjusting}
                        aria-label={copy.costsOpenLabel(question.label)}
                        onClick={() => setAdjusting((current) => !current)}
                        className="min-h-[44px] shrink-0 cursor-pointer rounded-[7px] border border-[var(--border-control)] bg-[var(--surface-card)] px-[13px] font-[family-name:var(--font-body)] text-[12px] font-medium text-[var(--accent-dark)] outline-offset-2 hover:border-[var(--accent)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                    >
                        {adjusting ? copy.costsClose : copy.costsOpen}
                    </button>
                )}
            </div>

            {adjusting || readOnly ? (
                <div className="flex flex-col gap-[10px]">
                    {readOnly ? null : (
                        <p className="m-0 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                            {copy.costsExplanation}
                        </p>
                    )}

                    <div className="grid gap-[10px] [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
                        <Field label={copy.repairCost}>
                            <Input
                                mono
                                inputMode="numeric"
                                value={cost}
                                readOnly={readOnly}
                                onChange={(event) => setCost(event.target.value)}
                                onBlur={() => {
                                    if (cost !== shownCost) {
                                        onAmountChange(parseEuroToCents(cost));
                                    }
                                }}
                            />
                        </Field>

                        <Field label={copy.urgency}>
                            <Select
                                value={urgency ?? ''}
                                disabled={readOnly}
                                onChange={(event) => {
                                    if (isUrgency(event.target.value)) {
                                        onUrgencyChange(event.target.value);
                                    }
                                }}
                            >
                                {urgency === null ? (
                                    <option value="">{copy.noChoice}</option>
                                ) : null}
                                {URGENCIES.map((option) => (
                                    <option key={option} value={option}>
                                        {URGENCY_LABELS[option]}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label={copy.nhg}>
                            <Select
                                value={nhg ?? ''}
                                disabled={readOnly}
                                onChange={(event) =>
                                    onNhgChange(
                                        isNhgClassification(event.target.value)
                                            ? event.target.value
                                            : null,
                                    )
                                }
                            >
                                <option value="">{copy.noChoice}</option>
                                {NHG_CLASSIFICATIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {NHG_CLASSIFICATION_LABELS[option]}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/** Ingesproken tekst komt achter wat er staat, met één spatie ertussen. */
function append(current: string, transcript: string): string {
    return current.trim().length === 0 ? transcript : `${current} ${transcript}`;
}

/** Twee spraakknoppen, één microfoon: beginnen betekent de andere stoppen. */
function exclusive(
    own: { toggle: () => void },
    other: { recording: boolean; stop: () => void },
): () => void {
    return () => {
        if (other.recording) {
            other.stop();
        }

        own.toggle();
    };
}

/** Hele centen uit de engine, hele euro's in het veld. */
function formatCost(cents: number): string {
    return formatEuro(Math.round(cents / 100));
}
